/** Состояние страницы «Аналитика МП» в адресе: ссылкой можно поделиться и снять скриншот.
 *
 *  Параметры (умолчание — параметра нет):
 *    mp    площадка ozon|wb (ozon): вкладки, карточки и пресеты дерева — по площадке (mp.ts);
 *          смена площадки — `mpPatch`
 *    p     пресет периода y|tw|pw|tm|pm|tq|pq|ytd|custom (y) — пишется ВСЕГДА (с 20.10.2026; раньше
 *          умолчанием был pm без p). Нет p, но есть from/to (старая ссылка) — custom: подпись не
 *          «Вчера», сравнение целых месяцев по-прежнему по месяцам; нет ни p, ни дат — y
 *    from, to  период YYYY-MM-DD — пишутся ВСЕГДА, чтобы ссылка «прошлый месяц» через месяц
 *          показывала тот же месяц; без них период считается от сегодня по пресету
 *    cmp   сравнение off|prev|yoy|custom (prev); cf, ct — даты своего периода сравнения
 *    legal юрлицо (нет — оба); alloc=1 — общие расходы по выручке
 *    tab   plan|summary|daily|tree|supplies|recon (summary); вкладки нет у площадки — summary.
 *          plan («План», с 20.10.2026) — обе площадки сразу: площадка, период, сравнение, юрлицо
 *          и карточка на ней не действуют (их параметры в адресе остаются для других вкладок).
 *          daily («По дням», с 22.10.2026) — площадка и юрлицо действуют, период/сравнение — нет
 *          (у вкладки свой месяц `month`)
 *    month месяц вкладок «План» и «По дням» YYYY-MM (общий); нет или не YYYY-MM — текущий (по
 *          сегодня). Текущий месяц в адрес не пишется (ссылка «текущий» остаётся текущей)
 *    channel заказы вкладки «По дням»: fbo|fbs; нет (или иное) — всего
 *    fc    режим прогноза вкладки «План» avg|last7|trend (last7 — «по 7 дням», в адрес не пишется)
 *    tp    пресет дерева площадки (первый); lv — свои уровни через запятую (тогда tp нет)
 *    lvoff выключенные «глазиком» разрезы через запятую (подмножество уровней; пишется с lv)
 *    q     поиск по дереву; sort abs|rev|cost|delta
 *    tsort сортировка дерева по столбцу (ключ `treeSort.ts`: rev, rev_qty, cost_pp, margin_d_pct…;
 *          неизвестный — сортировки нет); tdir=asc — по возрастанию (иначе по убыванию)
 *    grain month|week|day — тренд; by invoice|ship — поставки; iss=1 — только с проблемами
 *    card  карточка сущности `<вид>:<ключ>` (боковая панель поверх любой вкладки; вида нет у
 *          площадки — карточки нет): article:<offer_id> (у WB — vendor_code),
 *          supply:<номер заявки> (= obj_id витрины = order_number диагноза), campaign:<номер
 *          кампании> (= obj_id витрины = campaign_id таблиц рекламы; только цифры)
 */
import type { DailyChannel } from "@/api/mpAnalytics";
import type { Grain, OzFilters, SupplyBy, TreeSort } from "@/api/ozonAnalytics";
import { MP_CONFIGS, MP_IDS, type Mp, type TreePreset } from "./mp";
import {
  comparisonRange,
  isIsoDate,
  presetRange,
  type CmpMode,
  type PresetId,
  type Range,
} from "./period";
import { monthOfDay, parseMonthParam } from "./planCalc";
import { DEFAULT_FORECAST_MODE, parseForecastMode, type ForecastMode } from "./planForecast";
import { parseColSort, type ColSort } from "./treeSort";
import type { Hierarchy } from "@/lib/hierarchy";

// хелперы иерархии — в общем модуле (их использует и «Аналитика ОПТ»); здесь — реэкспорт для
// прежних импортов
export {
  MAX_LEVELS,
  activeLevels,
  addLevel,
  canRemoveLevel,
  moveLevel,
  removeLevel,
  setLevel,
  toggleLevelOff,
  unknownLevels,
} from "@/lib/hierarchy";
export type { Hierarchy } from "@/lib/hierarchy";

export type Tab = "plan" | "summary" | "daily" | "tree" | "supplies" | "recon";

export const TABS: { id: Tab; label: string }[] = [
  { id: "plan", label: "План" },
  { id: "summary", label: "Сводка" },
  { id: "daily", label: "По дням" },
  { id: "tree", label: "Дерево" },
  { id: "supplies", label: "Поставки" },
  { id: "recon", label: "Сверка" },
];

/** Вкладки площадки по порядку. */
export function tabsOf(mp: Mp): { id: Tab; label: string }[] {
  return TABS.filter((t) => MP_CONFIGS[mp].tabs.includes(t.id));
}

/** Пресеты дерева площадки (коды измерений — белый список бэкенда площадки). */
export function treePresetsOf(mp: Mp): TreePreset[] {
  return MP_CONFIGS[mp].treePresets;
}

function defaultTreePreset(mp: Mp): TreePreset {
  const p = MP_CONFIGS[mp].treePresets[0];
  if (!p) throw new Error(`У площадки ${mp} нет пресетов дерева`);
  return p;
}

/** Площадка из адреса: `mp=wb` — WB, нет параметра (или пусто) — Ozon. Иное значение — НЕ
 *  молча Ozon (ссылка могла быть на другую площадку): `unknown` — страница показывает ошибку
 *  «неизвестная площадка» с кнопкой «Открыть Ozon» и запросов не делает. */
export type MpParam = { mp: Mp; unknown: null } | { mp: null; unknown: string };

export function mpParam(params: URLSearchParams): MpParam {
  const v = params.get("mp");
  if (!v) return { mp: "ozon", unknown: null };
  if ((MP_IDS as readonly string[]).includes(v)) return { mp: v as Mp, unknown: null };
  return { mp: null, unknown: v };
}

/** Площадка из адреса; неизвестная — ошибка (страница проверяет `mpParam` раньше). */
export function parseMp(params: URLSearchParams): Mp {
  const r = mpParam(params);
  if (r.mp === null) throw new Error(`неизвестная площадка ${r.unknown}`);
  return r.mp;
}

/** Виды карточек сущности (боковая панель). */
export const CARD_KINDS = ["article", "supply", "campaign"] as const;
export type CardKind = (typeof CARD_KINDS)[number];

/** Открытая карточка: вид и ключ сущности (у артикула — offer_id, у поставки — номер заявки,
 *  у кампании — её номер). */
export interface CardRef {
  kind: CardKind;
  key: string;
}

/** `card=<вид>:<ключ>` → карточка. Санитизация, как у прочих параметров: неизвестный вид или
 *  вид, которого нет у площадки `mp`, нет `:`, пустой ключ или неканоничный номер кампании
 *  (не `^[1-9][0-9]*$`) — карточки нет. */
export function parseCard(v: string | null, mp: Mp): CardRef | null {
  if (!v) return null;
  const i = v.indexOf(":");
  if (i <= 0) return null;
  const kind = v.slice(0, i);
  const key = v.slice(i + 1);
  if (!key || !(MP_CONFIGS[mp].cards as readonly string[]).includes(kind)) return null;
  if (kind === "campaign" && !/^[1-9][0-9]*$/.test(key)) return null;
  return { kind: kind as CardKind, key };
}

/** Ключ `location.state`: карточку открыли из другой карточки (панель показывает «Назад»). */
export const CARD_BACK_STATE = "cardBack";

/** Открыть/закрыть карточку: меняется только `card`, остальной адрес не трогаем. */
export function cardPatch(card: CardRef | null): Patch {
  return { card: card ? `${card.kind}:${card.key}` : null };
}

export interface PageState {
  mp: Mp;
  preset: PresetId;
  period: Range;
  cmpMode: CmpMode;
  /** итоговый период сравнения (null — выключено или свой ещё не выбран) */
  cmp: Range | null;
  legal: string | null;
  alloc: boolean;
  tab: Tab;
  /** месяц вкладок «План» и «По дням» YYYY-MM */
  month: string;
  /** режим прогноза вкладки «План» */
  forecast: ForecastMode;
  /** заказы вкладки «По дням»: всего / FBO / FBS */
  channel: DailyChannel;
  /** id пресета дерева, если уровни совпадают с ним и выключенных разрезов нет */
  treePreset: string | null;
  /** все разрезы иерархии по порядку, включая выключенные */
  levels: string[];
  /** коды выключенных разрезов (подмножество levels, хотя бы один уровень активен) */
  levelsOff: string[];
  search: string;
  sort: TreeSort;
  /** сортировка дерева по столбцу (клик по заголовку); null — порядок бэкенда */
  colSort: ColSort | null;
  /** `tsort` из адреса, который не разобрался (прежний / неизвестный ключ) — `TreeTab` переписывает
   *  прежний ключ или показывает ошибку; null — нет или разобрался */
  colSortBad: string | null;
  grain: Grain;
  supplyBy: SupplyBy;
  onlyIssues: boolean;
  /** открытая карточка сущности; null — панели нет */
  card: CardRef | null;
}

function oneOf<T extends string>(v: string | null, allowed: readonly T[], dflt: T): T {
  return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : dflt;
}

const PRESET_IDS: PresetId[] = ["y", "tw", "pw", "tm", "pm", "tq", "pq", "ytd", "custom"];
const CMP_IDS: CmpMode[] = ["off", "prev", "yoy", "custom"];

/** Адрес → состояние. `today` — YYYY-MM-DD (для периода по пресету, когда дат в адресе нет);
 *  `dataTo` — конец данных витрины (из /meta): по нему обрезается период при расчёте сравнения. */
export function readState(
  params: URLSearchParams,
  today: string,
  dataTo: string | null = null,
): PageState {
  const mp = parseMp(params);
  const presets = treePresetsOf(mp);
  const from = params.get("from");
  const to = params.get("to");
  const hasDates = isIsoDate(from) && isIsoDate(to);
  // нет p (или чужое значение) при датах — старая ссылка с прежним умолчанием «прошлый месяц»:
  // подпись «Свой», а не «Вчера»; без дат — умолчание «вчера»
  let preset = oneOf(params.get("p"), PRESET_IDS, hasDates ? "custom" : "y");
  let period: Range;
  if (hasDates) {
    period = { from, to };
  } else {
    if (preset === "custom") preset = "y"; // свой период без дат — показывать нечего
    period = presetRange(preset, today);
  }

  const cmpMode = oneOf(params.get("cmp"), CMP_IDS, "prev");
  let cmp: Range | null = null;
  if (cmpMode === "custom") {
    const cf = params.get("cf");
    const ct = params.get("ct");
    cmp = isIsoDate(cf) && isIsoDate(ct) ? { from: cf, to: ct } : null;
  } else {
    cmp = comparisonRange(cmpMode, period, preset, dataTo);
  }

  const lv = params.get("lv");
  const tp = params.get("tp");
  let levels: string[];
  let treePreset: string | null;
  if (lv) {
    // свои уровни — как есть, не тихий сброс: коды не из meta.dims площадки вкладка «Дерево»
    // показывает ошибкой (unknownLevels) с кнопкой сброса
    levels = lv.split(",").filter(Boolean);
    treePreset = presets.find((p) => p.levels.join(",") === levels.join(","))?.id ?? null;
  } else {
    const p = presets.find((x) => x.id === tp) ?? defaultTreePreset(mp);
    levels = [...p.levels];
    treePreset = p.id;
  }
  // санитизация адреса: чужие коды и повторы отбрасываем; выключены все — lvoff не действует
  const lvoff = (params.get("lvoff") ?? "").split(",");
  let levelsOff = levels.filter((c, i) => levels.indexOf(c) === i && lvoff.includes(c));
  if (levelsOff.length > 0 && levels.every((c) => levelsOff.includes(c))) levelsOff = [];
  if (levelsOff.length > 0) treePreset = null;

  return {
    mp,
    preset,
    period,
    cmpMode,
    cmp,
    legal: params.get("legal") || null,
    alloc: params.get("alloc") === "1",
    tab: oneOf(params.get("tab"), MP_CONFIGS[mp].tabs, "summary"),
    month: parseMonthParam(params.get("month"), today),
    forecast: parseForecastMode(params.get("fc")),
    channel: oneOf(params.get("channel"), ["all", "fbo", "fbs"], "all"),
    treePreset,
    levels,
    levelsOff,
    search: params.get("q") ?? "",
    sort: oneOf(params.get("sort"), ["abs", "rev", "cost", "delta"], "abs"),
    colSort: parseColSort(params.get("tsort"), params.get("tdir")),
    colSortBad:
      params.get("tsort") !== null && parseColSort(params.get("tsort"), null) === null
        ? params.get("tsort")
        : null,
    grain: oneOf(params.get("grain"), ["month", "week", "day"], "month"),
    supplyBy: oneOf(params.get("by"), ["invoice", "ship"], "invoice"),
    onlyIssues: params.get("iss") === "1",
    card: parseCard(params.get("card"), mp),
  };
}

/** Патч адреса: строка — записать, null/"" — удалить параметр. */
export type Patch = Record<string, string | null>;

export function applyPatch(prev: URLSearchParams, patch: Patch): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === "") next.delete(k);
    else next.set(k, v);
  }
  return next;
}

/** Выбор пресета периода: пресет и даты пишем явно (стабильная ссылка; p без дат и даты без p
 *  читаются по-разному — см. readState). */
export function presetPatch(id: Exclude<PresetId, "custom">, today: string): Patch {
  const r = presetRange(id, today);
  return { p: id, from: r.from, to: r.to };
}

/** Месяц вкладок «План» и «По дням»: текущий — без параметра (ссылка остаётся «текущей»), иной — YYYY-MM. */
export function monthPatch(month: string, today: string): Patch {
  return { month: month === monthOfDay(today) ? null : month };
}

/** Режим прогноза вкладки «План»: по умолчанию — без параметра. */
export function forecastPatch(mode: ForecastMode): Patch {
  return { fc: mode === DEFAULT_FORECAST_MODE ? null : mode };
}

/** Заказы вкладки «По дням»: «всего» — без параметра. */
export function channelPatch(ch: DailyChannel): Patch {
  return { channel: ch === "all" ? null : ch };
}

/** Свой период. */
export function customPeriodPatch(r: Range): Patch {
  return { p: "custom", from: r.from, to: r.to };
}

/** Режим сравнения; свой период сравнения — с датами, иначе cf/ct убираем. */
export function cmpPatch(mode: CmpMode, custom: Range | null = null): Patch {
  return {
    cmp: mode === "prev" ? null : mode,
    cf: mode === "custom" && custom ? custom.from : null,
    ct: mode === "custom" && custom ? custom.to : null,
  };
}

/** Пресет дерева площадки (выключенные разрезы сбрасываются). */
export function treePresetPatch(id: string, mp: Mp): Patch {
  return { tp: id === defaultTreePreset(mp).id ? null : id, lv: null, lvoff: null };
}

/** Смена площадки. Измерения у площадок разные — дерево (tp, lv, lvoff) и поиск сбрасываются;
 *  вкладки нет у новой площадки — сводка; карточка закрывается. Юрлицо остаётся, если оно есть
 *  среди `legalNames` новой площадки (из её /meta), иначе сбрасывается; `legalNames = null` —
 *  /meta новой площадки не ответила, проверить не по чему: юрлицо в адресе НЕ трогаем (ошибку
 *  /meta страница покажет сама). Период, сравнение, сортировка, шаг графика — как были. */
export function mpPatch(s: PageState, mp: Mp, legalNames: readonly string[] | null): Patch {
  const patch: Patch = {
    mp: mp === "ozon" ? null : mp,
    tab: s.tab !== "summary" && MP_CONFIGS[mp].tabs.includes(s.tab) ? s.tab : null,
    card: null,
    tp: null,
    lv: null,
    lvoff: null,
    q: null,
  };
  if (legalNames !== null) {
    patch.legal = s.legal !== null && legalNames.includes(s.legal) ? s.legal : null;
  }
  return patch;
}

/** Свои уровни дерева: совпали с пресетом площадки и ничего не выключено — пишем пресет. */
export function levelsPatch(h: Hierarchy, mp: Mp): Patch {
  const off = h.levels.filter((c, i) => h.levels.indexOf(c) === i && h.off.includes(c));
  const p = treePresetsOf(mp).find((x) => x.levels.join(",") === h.levels.join(","));
  if (p && off.length === 0) return treePresetPatch(p.id, mp);
  return { tp: null, lv: h.levels.join(","), lvoff: off.length ? off.join(",") : null };
}

/** Фильтры для ручек бэкенда. */
export function toFilters(s: PageState): OzFilters {
  return {
    date_from: s.period.from,
    date_to: s.period.to,
    cmp_from: s.cmp?.from ?? null,
    cmp_to: s.cmp?.to ?? null,
    legal: s.legal,
    alloc: s.alloc,
  };
}
